// src/pages/Profile.jsx
import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import useApi from '../hooks/useApi';
import { useAuth } from '../context/AuthContext';

import { HomeIcon, CalendarIcon, Cog8ToothIcon, ArrowRightOnRectangleIcon } from '@heroicons/react/24/outline';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Animasyon varyantları
const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: { staggerChildren: 0.1, when: "beforeChildren" }
    }
};

const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
        y: 0,
        opacity: 1,
        transition: { type: "spring", stiffness: 100 }
    }
};

const cardVariants = {
    hover: {
        y: -5,
        scale: 1.02,
        transition: { duration: 0.3 }
    }
};

export default function Profile() {
    const [userProfile, setUserProfile] = useState({ name: '', surname: '', email: '' });
    const [tab, setTab] = useState('rezervasyon');
    const [showPasswordForm, setShowPasswordForm] = useState(false);
    const [oldPwd, setOldPwd] = useState('');
    const [newPwd, setNewPwd] = useState('');
    const [newPwd2, setNewPwd2] = useState('');
    const [reservations, setReservations] = useState([]);
    const [comments, setComments] = useState([]);
    const navigate = useNavigate();
    const { request } = useApi();
    const { user } = useAuth();

    useEffect(() => {
        const fetchReservations = async () => {
            try {
                const data = await request('/bookings/user');
                setReservations(data.data);
            } catch (err) {
                console.error('Rezervasyonlar alınamadı:', err.message);
            }
        };

        const fetchUserProfile = async () => {
            try {
                const res = await request('/users/profile');
                if (res.success) setUserProfile(res.data);
            } catch (error) {
                console.error('Kullanıcı bilgileri alınamadı:', error.message);
            }
        };

        const fetchComments = async () => {
            try {
                const res = await request('/comments/user');
                if (res.success) setComments(res.data);
            } catch (error) {
                console.error('Yorumlar alınamadı:', error.message);
                toast.error('Yorumlar yüklenemedi: ' + error.message);
            }
        };

        fetchReservations();
        fetchUserProfile();
        fetchComments();
    }, []);

    const formatDate = (dateString) => {
        if (!dateString) return 'Tarih yok';
        const date = new Date(dateString);
        if (isNaN(date)) return 'Geçersiz tarih';
        return date.toLocaleDateString('tr-TR', {
            day: '2-digit',
            month: 'long',
            year: 'numeric'
        });
    };

    const handleChangePassword = async () => {
        if (newPwd !== newPwd2) {
            toast.error('Yeni şifreler eşleşmiyor!');
            return;
        }

        try {
            await request('/auth/update-password', 'PUT', {
                currentPassword: oldPwd,
                newPassword: newPwd
            });

            toast.success('Şifren başarıyla değiştirildi.');
            setShowPasswordForm(false);
            setOldPwd('');
            setNewPwd('');
            setNewPwd2('');
        } catch (error) {
            toast.error(`Şifre değiştirilemedi: ${error.message}`);
        }
    };

    // Sidebar menüleri
    const menuItems = [
        { key: 'rezervasyon', label: 'Rezervasyonlarım', icon: CalendarIcon },
        { key: 'yorum', label: 'Yorumlarım', icon: HomeIcon },
        { key: 'ayarlar', label: 'Ayarlar', icon: Cog8ToothIcon },
        { key: 'logout', label: 'Çıkış Yap', icon: ArrowRightOnRectangleIcon },
    ];

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
            className="flex min-h-screen bg-gray-50"
        >
            {/* Sol Menü */}
            <motion.aside
                initial={{ x: -100 }}
                animate={{ x: 0 }}
                transition={{ type: "spring", stiffness: 100 }}
                className="w-64 bg-gradient-to-b from-[#241c15] via-[#523323] to-[#7F6B52] shadow-xl flex flex-col p-0"
            >
                <motion.div
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    className="px-6 py-8 border-b border-[#7F6B52] flex flex-col items-center gap-2"
                >
                    <motion.div
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className="w-16 h-16 rounded-full bg-gradient-to-r from-[#F9D923] via-[#FF914D] to-[#7F6B52] flex items-center justify-center text-2xl font-bold text-white shadow-inner mb-2"
                    >
                        {userProfile.name?.[0] || ''}{userProfile.surname?.[0] || ''}
                    </motion.div>
                    <div className="text-lg font-bold text-[#F9D923]">Profilim</div>
                    <div className="text-xs text-[#FF914D] break-all text-center">{userProfile.email}</div>
                </motion.div>
                <nav className="flex-1 flex flex-col gap-1 mt-4 px-2">
                    {menuItems.map((item, index) => (
                        <motion.button
                            key={item.key}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: 0.3 + index * 0.1 }}
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            onClick={() => {
                                if (item.key === 'logout') { localStorage.removeItem('auth'); navigate('/login'); return; }
                                setTab(item.key);
                            }}
                            className={`flex items-center gap-3 w-full px-5 py-3 rounded-lg font-semibold transition-all duration-200 text-base group 
                                ${tab === item.key
                                    ? 'bg-gradient-to-r from-[#F9D923] via-[#FF914D] to-[#7F6B52] shadow border-l-4 border-[#F9D923] text-[#241c15]'
                                    : 'text-white hover:bg-[#523323] hover:shadow'}
                            `}
                        >
                            <item.icon className={`w-6 h-6 transition ${tab === item.key ? 'text-[#241c15]' : 'text-[#F9D923] group-hover:text-[#FF914D]'}`} />
                            <span>{item.label}</span>
                        </motion.button>
                    ))}
                </nav>
            </motion.aside>

            {/* İçerik */}
            <motion.main
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.4 }}
                className="flex-1 p-8 overflow-auto bg-gradient-to-br from-[#f5f5f5] via-[#e2d1c3] to-[#cbb396]"
            >
                <div className="max-w-4xl mx-auto">
                    {/* Sekme içerikleri */}
                    <AnimatePresence mode="wait">
                        {tab === 'rezervasyon' && (
                            <motion.div
                                key="rezervasyon"
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -20 }}
                                transition={{ duration: 0.3 }}
                                className="space-y-6"
                            >
                                <h2 className="text-2xl font-bold text-[#F9D923]">Rezervasyonlarım</h2>
                                {reservations.length === 0 && (
                                    <motion.div
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        className="bg-white/60 p-6 rounded-xl shadow text-center text-[#523323]"
                                    >
                                        Henüz rezervasyon bulunmamaktadır.
                                    </motion.div>
                                )}
                                <motion.div
                                    variants={containerVariants}
                                    initial="hidden"
                                    animate="visible"
                                    className="space-y-6"
                                >
                                    {reservations.map((res, i) => (
                                        <motion.div
                                            key={i}
                                            variants={cardVariants}
                                            whileHover="hover"
                                            className="bg-white/60 rounded-xl shadow p-5 border border-[#F9D923]/30"
                                        >
                                            <h3 className="text-xl font-bold text-[#523323] mb-2">
                                                {res.unit?.name || 'Pansiyon'}
                                            </h3>
                                            <p className="text-[#7F6B52]">
                                                <strong>Giriş:</strong> {formatDate(res.startDate)}
                                            </p>
                                            <p className="text-[#7F6B52]">
                                                <strong>Çıkış:</strong> {formatDate(res.endDate)}
                                            </p>
                                            <p className="text-[#7F6B52]">
                                                <strong>Kişi Sayısı:</strong> {res.customerCount}
                                            </p>
                                            <p className={`text-sm font-semibold mt-2 ${res.status === true ? 'text-green-600' :
                                                    res.status === false ? 'text-red-600' :
                                                        'text-yellow-600'
                                                }`}>
                                                {
                                                    res.status === true ? 'Onaylandı' :
                                                        res.status === false ? 'Reddedildi' :
                                                            'Bekliyor'
                                                }
                                            </p>
                                        </motion.div>

                                    ))}
                                </motion.div>
                            </motion.div>
                        )}

                        {tab === 'yorum' && (
                            <motion.div
                                key="yorum"
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -20 }}
                                transition={{ duration: 0.3 }}
                                className="space-y-6"
                            >
                                <h2 className="text-2xl font-bold text-[#F9D923]">Yorumlarım</h2>
                                {comments.length === 0 && (
                                    <motion.div
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        className="bg-white/60 p-6 rounded-xl shadow text-center text-[#523323]"
                                    >
                                        Henüz yorum bulunmamaktadır.
                                    </motion.div>
                                )}
                                <motion.div
                                    variants={containerVariants}
                                    initial="hidden"
                                    animate="visible"
                                    className="space-y-6"
                                >
                                    {comments.map((comment, i) => (
                                        <motion.div
                                            key={i}
                                            variants={cardVariants}
                                            whileHover="hover"
                                            className="bg-white/60 rounded-xl shadow p-5 border border-[#F9D923]/30"
                                        >
                                            <div className="font-bold text-[#523323]">{comment?.Accommodation?.name || 'Pansiyon'}</div>
                                            <div className="text-[#7F6B52] text-sm mt-1">{comment.commentBody || 'Yorum yok'}</div>
                                        </motion.div>
                                    ))}
                                </motion.div>
                            </motion.div>
                        )}

                        {tab === 'ayarlar' && (
                            <motion.div
                                key="ayarlar"
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -20 }}
                                transition={{ duration: 0.3 }}
                                className="max-w-lg mx-auto mt-12 space-y-8"
                            >
                                <motion.div
                                    whileHover={{ scale: 1.01 }}
                                    className="bg-white/60 rounded-2xl shadow-xl p-8 flex flex-col items-center gap-2 border border-[#F9D923]/30"
                                >
                                    <motion.div
                                        whileHover={{ rotate: 5, scale: 1.05 }}
                                        whileTap={{ rotate: -5, scale: 0.95 }}
                                        className="w-24 h-24 rounded-full bg-gradient-to-r from-[#F9D923] via-[#FF914D] to-[#7F6B52] flex items-center justify-center text-3xl font-bold text-white mb-4 shadow-inner"
                                    >
                                        {userProfile.name?.[0] || ''}{userProfile.surname?.[0] || ''}
                                    </motion.div>
                                    <div className="text-2xl font-bold text-[#F9D923] mb-1">{userProfile.name} {userProfile.surname}</div>
                                    <div className="text-[#7F6B52] text-lg mb-2">{userProfile.email}</div>
                                </motion.div>

                                <motion.div
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    transition={{ delay: 0.2 }}
                                    className="bg-white/60 rounded-2xl shadow-xl p-8 border border-[#F9D923]/30"
                                >
                                    <motion.button
                                        whileHover={{ scale: 1.02 }}
                                        whileTap={{ scale: 0.98 }}
                                        className="w-full py-3 rounded-xl font-bold text-lg bg-gradient-to-r from-[#F9D923] via-[#FF914D] to-[#7F6B52] text-white shadow-lg hover:brightness-110 hover:scale-105 transition-all duration-300 border-none mb-4"
                                        onClick={() => setShowPasswordForm((v) => !v)}
                                    >
                                        {showPasswordForm ? 'Şifre Değiştir (Kapat)' : 'Şifreyi Değiştir'}
                                    </motion.button>

                                    <AnimatePresence>
                                        {showPasswordForm && (
                                            <motion.form
                                                initial={{ height: 0, opacity: 0 }}
                                                animate={{ height: 'auto', opacity: 1 }}
                                                exit={{ height: 0, opacity: 0 }}
                                                transition={{ duration: 0.3, ease: "easeInOut" }}
                                                className="overflow-hidden space-y-4"
                                                onSubmit={async (e) => {
                                                    e.preventDefault();
                                                    if (newPwd !== newPwd2) {
                                                        alert('Yeni şifreler eşleşmiyor!');
                                                        return;
                                                    }
                                                    try {
                                                        await request('/auth/update-password', 'PUT', {
                                                            currentPassword: oldPwd,
                                                            newPassword: newPwd
                                                        });
                                                        alert('Şifreniz başarıyla değiştirildi.');
                                                        setShowPasswordForm(false);
                                                        setOldPwd(''); setNewPwd(''); setNewPwd2('');
                                                    } catch (err) {
                                                        alert('Şifre değiştirilemedi: ' + (err.message || ''));
                                                    }
                                                }}
                                            >
                                                <motion.div
                                                    variants={itemVariants}
                                                >
                                                    <label className="block mb-1 font-medium text-[#523323]">Mevcut Şifre</label>
                                                    <motion.input
                                                        whileFocus={{ scale: 1.01 }}
                                                        type="password"
                                                        className="w-full px-4 py-3 border border-[#F9D923]/30 bg-white/40 rounded-xl text-[#241c15] font-medium placeholder-[#523323]/60 focus:outline-none focus:ring-2 focus:ring-[#F9D923] shadow-sm"
                                                        value={oldPwd}
                                                        onChange={e => setOldPwd(e.target.value)}
                                                        required
                                                    />
                                                </motion.div>
                                                <motion.div
                                                    variants={itemVariants}
                                                >
                                                    <label className="block mb-1 font-medium text-[#523323]">Yeni Şifre</label>
                                                    <motion.input
                                                        whileFocus={{ scale: 1.01 }}
                                                        type="password"
                                                        className="w-full px-4 py-3 border border-[#F9D923]/30 bg-white/40 rounded-xl text-[#241c15] font-medium placeholder-[#523323]/60 focus:outline-none focus:ring-2 focus:ring-[#F9D923] shadow-sm"
                                                        value={newPwd}
                                                        onChange={e => setNewPwd(e.target.value)}
                                                        required
                                                    />
                                                </motion.div>
                                                <motion.div
                                                    variants={itemVariants}
                                                >
                                                    <label className="block mb-1 font-medium text-[#523323]">Yeni Şifre (Tekrar)</label>
                                                    <motion.input
                                                        whileFocus={{ scale: 1.01 }}
                                                        type="password"
                                                        className="w-full px-4 py-3 border border-[#F9D923]/30 bg-white/40 rounded-xl text-[#241c15] font-medium placeholder-[#523323]/60 focus:outline-none focus:ring-2 focus:ring-[#F9D923] shadow-sm"
                                                        value={newPwd2}
                                                        onChange={e => setNewPwd2(e.target.value)}
                                                        required
                                                    />
                                                </motion.div>
                                                <motion.div
                                                    variants={containerVariants}
                                                    className="flex gap-4 mt-2"
                                                >
                                                    <motion.button
                                                        variants={itemVariants}
                                                        whileHover={{ scale: 1.05 }}
                                                        whileTap={{ scale: 0.95 }}
                                                        type="button"
                                                        className="flex-1 bg-[#7F6B52] text-white px-6 py-3 rounded-xl font-semibold hover:scale-105 transition"
                                                        onClick={() => setShowPasswordForm(false)}
                                                    >
                                                        İptal
                                                    </motion.button>
                                                    <motion.button
                                                        variants={itemVariants}
                                                        whileHover={{ scale: 1.05 }}
                                                        whileTap={{ scale: 0.95 }}
                                                        type="submit"
                                                        className="flex-1 bg-gradient-to-r from-[#F9D923] via-[#FF914D] to-[#7F6B52] text-white px-6 py-3 rounded-xl font-semibold hover:scale-105 transition"
                                                    >
                                                        Şifreyi Değiştir
                                                    </motion.button>
                                                </motion.div>
                                            </motion.form>
                                        )}
                                    </AnimatePresence>
                                </motion.div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
            </motion.main>
            <ToastContainer position="top-right" autoClose={3000} />
        </motion.div>
    );
}